import React from 'react';
import {
  CustomInputContainer,
  CustomInputLabel,
  ErrorMessage,
} from './CustomInputs.styles';
import { TextField, TextFieldProps, useTheme } from '@mui/material';
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CalendarMonth, KeyboardArrowDown } from '@mui/icons-material';
import { format } from 'date-fns';

interface Props {
  label: string;
  value: string;
  onChange: (newDate: string) => void;
  disableBeforeDate?: string;
  errorMessage?: string;
  type?: 'DateTime' | 'Date';
  placeholder?: 'Today' | 'Select';
  allowPastDates?: boolean;
}

const CustomDatePicker: React.FC<Props> = ({
  onChange,
  value,
  label,
  disableBeforeDate,
  errorMessage,
  type = 'DateTime',
  placeholder = 'Today',
  allowPastDates = false,
}) => {
  const theme = useTheme();

  const disableDates = (date: Date): boolean => {
    if (disableBeforeDate) {
      return date <= new Date(disableBeforeDate);
    }
    return false;
  };

  return (
    <CustomInputContainer>
      <CustomInputLabel theme={theme as any}>{label}</CustomInputLabel>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {type === 'DateTime' ? (
          <DateTimePicker
            components={{
              OpenPickerIcon: value ? CalendarMonth : KeyboardArrowDown,
            }}
            shouldDisableDate={disableBeforeDate ? disableDates : undefined}
            disablePast={!allowPastDates && !disableBeforeDate}
            value={value ? new Date(value) : null}
            onChange={(e) => {
              if (!e) {
                return;
              }

              if (!new Date(e).valueOf()) {
                return;
              }

              onChange(e?.toISOString() || '');
            }}
            PopperProps={getStyles(theme).PopperProps}
            PaperProps={getStyles(theme).PaperProps}
            InputProps={{
              ...getStyles(theme).InputProps,
              sx: {
                ...getStyles(theme).InputProps.sx,
                padding: '0 4px 0 0',
              },
            }}
            renderInput={(params) => {
              return (
                <RenderInputTextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    placeholder,
                  }}
                  error={Boolean(errorMessage)}
                />
              );
            }}
          />
        ) : (
          <DatePicker
            components={{
              OpenPickerIcon: value ? CalendarMonth : KeyboardArrowDown,
            }}
            shouldDisableDate={disableBeforeDate ? disableDates : undefined}
            disablePast={!allowPastDates && !disableBeforeDate}
            value={value ? new Date(value) : null}
            onChange={(e) => {
              if (!e) {
                return;
              }

              if (!new Date(e).valueOf()) {
                return;
              }

              onChange(e ? format(e, 'MM/dd/yyyy') : '');
            }}
            PopperProps={getStyles(theme).PopperProps}
            PaperProps={getStyles(theme).PaperProps}
            InputProps={{
              ...getStyles(theme).InputProps,
              sx: {
                ...getStyles(theme).InputProps.sx,
                padding: '0 4px 0 0',
              },
            }}
            renderInput={(params) => {
              return (
                <RenderInputTextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    placeholder,
                  }}
                  error={Boolean(errorMessage)}
                />
              );
            }}
          />
        )}

        {errorMessage && (
          <ErrorMessage theme={theme as any}>{errorMessage}</ErrorMessage>
        )}
      </LocalizationProvider>
    </CustomInputContainer>
  );
};

const RenderInputTextField = (props: TextFieldProps) => {
  const theme = useTheme();
  const hasError = Boolean(props.error);
  const isDark = theme.custom?.isDark || false;

  return (
    <TextField
      {...props}
      sx={{
        '& .MuiOutlinedInput-root': {
          border: `1px solid ${
            hasError
              ? '#FF5C77'
              : theme.custom?.stroke || (isDark ? '#404040' : '#D7D9D6')
          }`,
          backgroundColor:
            theme.custom?.background1 || (isDark ? '#121212' : '#ffffff'),
          height: '56px',
          borderRadius: '8px',
          transition:
            'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            border: `1px solid ${
              hasError ? '#FF5C77' : theme.custom?.primary || '#FF6A00'
            }`,
          },
          '&.Mui-focused': {
            border: `1px solid ${
              hasError ? '#FF5C77' : theme.custom?.primary || '#FF6A00'
            }`,
            boxShadow: isDark
              ? `0 0 0 2px ${theme.custom?.primary || '#FF6A00'}20`
              : `0 0 0 2px ${theme.custom?.primary || '#FF6A00'}30`,
            '& fieldset': {
              border: 'none',
            },
          },
        },
        width: '100%',
        '& input': {
          padding: '16px',
          fontFamily: 'Manrope',
          fontSize: '13px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '24px',
          color: theme.custom?.text || (isDark ? '#FFFFFF' : '#333'),
          fontFeatureSettings: '"liga" off, "clig" off',
          '&::placeholder': {
            opacity: 0.7,
            color: theme.custom?.textLight || (isDark ? '#B0B0B0' : '#858585'),
            fontWeight: 700,
          },
        },
        '& fieldset': {
          borderRadius: '8px !important',
          border: 'none',
        },
      }}
    />
  );
};

const getStyles = (theme: any) => {
  const isDark = theme.custom?.isDark || false;

  return {
    PopperProps: {
      sx: {
        boxShadow: isDark
          ? 'rgba(0, 0, 0, 0.8) 0px 4px 20px 0px'
          : 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
        '& .MuiPaper-root': {
          border: isDark
            ? `1px solid ${theme.custom?.stroke || '#404040'}`
            : `1px solid ${theme.custom?.stroke || '#E0E0E0'}`,
        },
        // Global override for ALL elements within the date/time picker
        '& *': {
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
          fontFamily: 'Manrope !important',
        },
        // Specific overrides for known problematic elements
        '& .MuiClock-clock *': {
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
        },
        '& .MuiTimeClock-root *': {
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
        },
        '& [class*="Clock"] *': {
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
        },
        '& [class*="Time"] *': {
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
        },
      },
      // Apply styles directly to the popper element
      style: {
        color: isDark ? '#FFFFFF' : '#333333',
        fontFamily: 'Manrope',
      },
    },
    PaperProps: {
      sx: {
        backgroundColor:
          theme.custom?.background1 || (isDark ? '#121212' : '#ffffff'),
        borderRadius: '12px',
        border: isDark
          ? `1px solid ${theme.custom?.stroke || '#404040'}`
          : 'none',

        // Apply universal styling to all children
        '& *': {
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
          fontFamily: 'Manrope !important',
        },

        // Clock styling
        '& .MuiClock-pin': {
          backgroundColor: theme.custom?.primary || '#FF6A00',
        },
        '& .MuiClockPointer-root': {
          backgroundColor: theme.custom?.primary || '#FF6A00',
        },
        '& .MuiClockPointer-thumb': {
          backgroundColor: theme.custom?.primary || '#FF6A00',
          border: `2px solid ${
            theme.custom?.background1 || (isDark ? '#121212' : '#ffffff')
          }`,
        },

        // General button styling
        '& .MuiButtonBase-root': {
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
          fontFamily: 'Manrope',
          '&.Mui-selected': {
            backgroundColor: theme.custom?.primary || '#FF6A00',
            color: `${theme.custom?.textCTA || '#FFFFFF'} !important`,
          },
          '&:hover': {
            backgroundColor: isDark
              ? `${theme.custom?.primary || '#FF6A00'}20`
              : `${theme.custom?.primary || '#FF6A00'}10`,
          },
        },

        // Calendar container
        '& .MuiCalendarPicker-root, & .MuiDateCalendar-root': {
          backgroundColor:
            theme.custom?.background1 || (isDark ? '#121212' : '#ffffff'),
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
        },

        // Individual date buttons - this is the key fix
        '& .MuiPickersDay-root, & .MuiDateCalendar-root .MuiPickersDay-root': {
          backgroundColor: 'transparent !important',
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
          border: 'none !important',
          fontWeight: 500,
          fontFamily: 'Manrope',
          '&:hover': {
            backgroundColor: `${
              isDark
                ? `${theme.custom?.primary || '#FF6A00'}25`
                : `${theme.custom?.primary || '#FF6A00'}15`
            } !important`,
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
          },
          '&.Mui-selected': {
            backgroundColor: `${theme.custom?.primary || '#FF6A00'} !important`,
            color: `${theme.custom?.textCTA || '#FFFFFF'} !important`,
            '&:hover': {
              backgroundColor: `${
                theme.custom?.primaryDark || '#DC5C01'
              } !important`,
            },
          },
          '&.Mui-disabled': {
            color: `${
              isDark
                ? `${theme.custom?.textLight || '#B0B0B0'}50`
                : `${theme.custom?.textLight || '#666666'}60`
            } !important`,
            backgroundColor: 'transparent !important',
          },
        },

        // Calendar header styling
        '& .MuiPickersCalendarHeader-root, & .MuiDateCalendar-root .MuiPickersCalendarHeader-root':
          {
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
            '& .MuiButtonBase-root': {
              color: `${
                theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
              } !important`,
              '&:hover': {
                backgroundColor: isDark
                  ? `${theme.custom?.primary || '#FF6A00'}25`
                  : `${theme.custom?.primary || '#FF6A00'}15`,
              },
            },
          },

        // Week day labels
        '& .MuiDayCalendar-weekDayLabel': {
          color: `${
            theme.custom?.textLight || (isDark ? '#B0B0B0' : '#666666')
          } !important`,
          fontWeight: 600,
          fontFamily: 'Manrope',
        },

        // Time clock styling with universal overrides
        '& .MuiTimeClock-root': {
          backgroundColor: `${
            theme.custom?.background1 || (isDark ? '#121212' : '#ffffff')
          } !important`,
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
          '& *': {
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
            fontFamily: 'Manrope !important',
          },
          '& .MuiClock-root': {
            backgroundColor: `${
              theme.custom?.background1 || (isDark ? '#121212' : '#ffffff')
            } !important`,
            '& *': {
              color: `${
                theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
              } !important`,
            },
          },
          '& .MuiClock-clock': {
            backgroundColor: `${
              isDark
                ? theme.custom?.background2 || '#1E1E1E'
                : theme.custom?.background3 || '#EEEEEE'
            } !important`,
            border: `${
              isDark ? `1px solid ${theme.custom?.stroke || '#404040'}` : 'none'
            } !important`,
            '& *': {
              color: `${
                theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
              } !important`,
            },
          },
        },

        // Toolbar styling
        '& .MuiPickersToolbar-root': {
          backgroundColor:
            theme.custom?.background2 || (isDark ? '#1E1E1E' : '#F5F5F5'),
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
          borderBottom: isDark
            ? `1px solid ${theme.custom?.stroke || '#404040'}`
            : 'none',
          '& *': {
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
            fontFamily: 'Manrope !important',
          },
        },

        // DateTime picker toolbar
        '& .MuiDateTimePickerToolbar-dateContainer': {
          '& .MuiButtonBase-root': {
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
            fontFamily: 'Manrope',
            '&:hover': {
              backgroundColor: isDark
                ? `${theme.custom?.primary || '#FF6A00'}20`
                : `${theme.custom?.primary || '#FF6A00'}10`,
            },
          },
        },

        // Time input fields
        '& .MuiDateTimePickerToolbar-timeContainer': {
          '& .MuiButtonBase-root': {
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
            fontFamily: 'Manrope',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: isDark
                ? `${theme.custom?.primary || '#FF6A00'}20`
                : `${theme.custom?.primary || '#FF6A00'}10`,
            },
            '&.Mui-selected': {
              backgroundColor: theme.custom?.primary || '#FF6A00',
              color: `${theme.custom?.textCTA || '#FFFFFF'} !important`,
            },
          },
        },

        // Digital clock styling
        '& .MuiMultiSectionDigitalClock-root': {
          backgroundColor:
            theme.custom?.background1 || (isDark ? '#121212' : '#ffffff'),
          '& .MuiMultiSectionDigitalClockSection-root': {
            '& .MuiMenuItem-root': {
              color: `${
                theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
              } !important`,
              fontFamily: 'Manrope',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: isDark
                  ? `${theme.custom?.primary || '#FF6A00'}25`
                  : `${theme.custom?.primary || '#FF6A00'}15`,
              },
              '&.Mui-selected': {
                backgroundColor: theme.custom?.primary || '#FF6A00',
                color: `${theme.custom?.textCTA || '#FFFFFF'} !important`,
              },
            },
          },
        },

        // Additional time picker elements - catch-all for any missed elements
        '& .MuiClock-wrapper': {
          backgroundColor: `${
            theme.custom?.background1 || (isDark ? '#121212' : '#ffffff')
          } !important`,
          '& *': {
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
          },
        },

        // Target any button elements within time picker
        '& .MuiTimeClock-root .MuiButtonBase-root': {
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
          fontFamily: 'Manrope !important',
        },

        // Target specific elements that might have default styling
        '& [role="listbox"] .MuiButtonBase-root': {
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
          backgroundColor: 'transparent !important',
          '&:hover': {
            backgroundColor: `${
              isDark
                ? `${theme.custom?.primary || '#FF6A00'}20`
                : `${theme.custom?.primary || '#FF6A00'}10`
            } !important`,
          },
          '&.Mui-selected': {
            backgroundColor: `${theme.custom?.primary || '#FF6A00'} !important`,
            color: `${theme.custom?.textCTA || '#FFFFFF'} !important`,
          },
        },

        // Target time picker navigation buttons
        '& .MuiPickersArrowSwitcher-root': {
          '& .MuiIconButton-root': {
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
            '&:hover': {
              backgroundColor: `${
                isDark
                  ? `${theme.custom?.primary || '#FF6A00'}20`
                  : `${theme.custom?.primary || '#FF6A00'}10`
              } !important`,
            },
          },
        },

        // Target any text elements within time picker
        '& .MuiTimeClock-root .MuiTypography-root': {
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
          fontFamily: 'Manrope !important',
        },

        // Ensure clock face numbers are visible
        '& .MuiClock-squareMask': {
          '& span': {
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
            fontFamily: 'Manrope !important',
            fontWeight: '500 !important',
          },
        },

        // Target mobile time picker elements
        '& .MuiMobileTimePicker-root': {
          backgroundColor: `${
            theme.custom?.background1 || (isDark ? '#121212' : '#ffffff')
          } !important`,
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
        },

        // Target desktop time picker elements
        '& .MuiDesktopTimePicker-root': {
          backgroundColor: `${
            theme.custom?.background1 || (isDark ? '#121212' : '#ffffff')
          } !important`,
          color: `${
            theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
          } !important`,
        },

        // Target the layout content wrapper specifically for time picker
        '& .MuiPickersLayout-contentWrapper': {
          backgroundColor: `${
            theme.custom?.background1 || (isDark ? '#121212' : '#ffffff')
          } !important`,
          '& *': {
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
            fontFamily: 'Manrope !important',
          },
        },

        // Today button styling
        '& .MuiPickersActionBar-root': {
          backgroundColor: isDark
            ? theme.custom?.background2 || '#1E1E1E'
            : 'transparent',
          borderTop: isDark
            ? `1px solid ${theme.custom?.stroke || '#404040'}`
            : 'none',
          '& .MuiButton-root': {
            color: `${theme.custom?.primary || '#FF6A00'} !important`,
            fontFamily: 'Manrope',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: isDark
                ? `${theme.custom?.primary || '#FF6A00'}20`
                : `${theme.custom?.primary || '#FF6A00'}10`,
            },
          },
        },

        // Time field input styling
        '& .MuiTimeField-root': {
          '& .MuiInputBase-root': {
            backgroundColor:
              theme.custom?.background1 || (isDark ? '#121212' : '#ffffff'),
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
            fontFamily: 'Manrope',
            border: `1px solid ${
              theme.custom?.stroke || (isDark ? '#404040' : '#E0E0E0')
            }`,
            borderRadius: '8px',
            '&:hover': {
              borderColor: theme.custom?.primary || '#FF6A00',
            },
            '&.Mui-focused': {
              borderColor: theme.custom?.primary || '#FF6A00',
              boxShadow: isDark
                ? `0 0 0 2px ${theme.custom?.primary || '#FF6A00'}20`
                : `0 0 0 2px ${theme.custom?.primary || '#FF6A00'}30`,
            },
          },
          '& input': {
            color: `${
              theme.custom?.text || (isDark ? '#FFFFFF' : '#333333')
            } !important`,
            fontFamily: 'Manrope',
          },
        },
      },
    },
    InputProps: {
      sx: {
        borderRadius: '8px',
        '& .MuiInputAdornment-root': {
          marginRight: '8px',
        },
        '& .MuiSvgIcon-root': {
          fontSize: '20px',
          color: theme.custom?.text || (isDark ? '#FFFFFF' : '#333333'),
          transition: 'color 0.2s ease-in-out',
          '&:hover': {
            color: theme.custom?.primary || '#FF6A00',
          },
        },
      },
    },
  };
};

export default CustomDatePicker;
